import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import axios from 'axios'
import { makeAutoObservable } from 'mobx'
import qs from 'qs'

import { envUtils } from '../envUtils'

const BASE_URL = (() => {
  if (envUtils.isLive) {
    return `https://remote-storage.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isStaging) {
    return `https://remote-storage-staging.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://remote-storage-dev.${domainProvider.TARGET_DOMAIN}`
})()

export const remoteStorageAxios = axios.create({
  baseURL: BASE_URL,
  paramsSerializer: (params) => {
    return qs.stringify(params)
  },
})

type RemoteStorageItem = {
  visitedWorksheetBoardView: boolean

  ONE_CLICK_WORKSHEET_OPTIONS: {
    problemRequestType: common.ProblemRequestType
    problemCount: '5' | '10' | '15'
  }
  STARTED_NEW_DESIGN_FLAT: boolean
  VISITED_ACADEMY_INFO_UPDATE_PAGE: boolean
}

class RemoteStorageService implements RemoteStorageItem {
  private relationId: string | null = null

  visitedWorksheetBoardView = false

  ONE_CLICK_WORKSHEET_OPTIONS = {} as {
    problemRequestType: 'SIMILAR' | 'WRONG' | 'RANDOM'
    problemCount: '5' | '10' | '15'
  }
  STARTED_NEW_DESIGN_FLAT = false
  VISITED_ACADEMY_INFO_UPDATE_PAGE = false

  constructor() {
    makeAutoObservable(this)
  }

  get isReady() {
    return !!this.relationId
  }

  initRelationId = (relationId) => {
    if (!relationId) return

    this.relationId = relationId
  }

  get = async <K extends keyof RemoteStorageItem>(...keys: K[]) => {
    if (!this.isReady) {
      return
    }

    const { data } = await remoteStorageAxios.get(`/storage/${this.relationId}`, {
      params: {
        keys: keys.join(','),
      },
    })

    Object.entries(data as Record<string, unknown>).forEach(([k, v]) => {
      this[k] = v
    })

    return data as Record<K, RemoteStorageItem[K]>
  }

  patch = async <K extends keyof RemoteStorageItem, V extends RemoteStorageItem[K]>(k: K, v: V) => {
    if (!this.isReady) {
      return
    }

    if (k in this) {
      if (typeof this[k] === 'object' && this[k] !== null) {
        this[k] = {
          ...(this[k] as Record<string, unknown>),
          ...(v as Record<string, unknown>),
        } as this[K]
      } else {
        this[k] = v as unknown as this[K]
      }

      await remoteStorageAxios.patch(`/storage/${this.relationId}`, { [k]: this[k] })
    }
  }

  put = async <K extends keyof RemoteStorageItem, V extends RemoteStorageItem[K]>(k: K, v: V) => {
    if (!this.isReady) {
      return
    }

    if (k in this) {
      this[k] = v as unknown as this[K]

      await remoteStorageAxios.patch(`/storage/${this.relationId}`, { [k]: this[k] })
    }
  }

  onLogout = async () => {}
}

export const remoteStorageService = new RemoteStorageService()
